import { FC, useEffect, useState, useCallback } from 'react'
import Icon from '@forwardflow/ui/Icons'
import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '@forwardflow/hooks/customHooks/campaign'
import { ICampaignQuestions } from '@forwardflow/types/organization'
import CampaignService from '@forwardflow/services/CampaignService'
import { useParams } from 'react-router-dom'
import { Form, message, Popconfirm } from 'antd'
import QuestionSubmitForm from '@forwardflow/forms/QuestionSubmitForm'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { RemoveQualifyingQuestionModal } from '@forwardflow/modals/RemoveQualifyingQuestionModal'

const ItemType = {
   QUESTION: 'question',
}

const DraggableQuestion = ({
   item,
   index,
   moveQuestion,
   onDelete,
   onClick,
}: {
   item: ICampaignQuestions
   index: number
   moveQuestion: (fromIndex: number, toIndex: number) => void
   onDelete: () => void
   onClick?: () => void
}) => {
   const handleDelete = () => {
      onDelete()
   }
   const [, ref] = useDrag({
      type: ItemType.QUESTION,
      item: { index },
   })

   const [, drop] = useDrop({
      accept: ItemType.QUESTION,
      hover: (draggedItem: { index: number }) => {
         if (draggedItem.index !== index) {
            moveQuestion(draggedItem.index, index)
            draggedItem.index = index
         }
      },
   })

   return (
      <div ref={(node) => ref(drop(node))}>
         <div className="flex items-center gap-3 px-10 py-5 bg-primary/10">
            <Icon name="Drag" className="mx-1 cursor-pointer" />
            <p className="text-[14px] mr-4">{index + 1}</p>
            <p className="text-[12px] text-white/70 gradient-text mr-auto flex-1">
               {item.question}
            </p>
            <div className="flex items-center justify-between">
               <div className="flex items-center gap-x-3">
                  <div onClick={onClick}>
                     <Icon
                        name="Edit"
                        className="mx-1 scale-125 opacity-50 cursor-pointer hover:opacity-100"
                     />
                  </div>

                  <Popconfirm
                     title="Delete the qualifying question"
                     description="Are you sure to delete this question?"
                     onConfirm={handleDelete}
                     onCancel={() => {
                        console.log('Cancel')
                     }}
                     okText="Yes"
                     cancelText="No"
                     overlayClassName="ff-popconfirm"
                  >
                     <div>
                        <Icon
                           name="Delete"
                           className="cursor-pointer opacity-70 fill-danger hover:opacity-100"
                        />
                     </div>
                  </Popconfirm>
               </div>
            </div>
         </div>
         <RemoveQualifyingQuestionModal onDelete={handleDelete} />
      </div>
   )
}
const QualifyingQuestions: FC = () => {
   const { title } = useParams()
   const [isButtonLoading, setButtonLoading] = useState(false)
   const [option, setOption] = useState<'buyer' | 'seller'>('buyer')
   const {
      data: CampaignDetails,
      isLoading,
      mutate,
   } = useGetActiveLeadSourceDetails(title as string)
   const [showQuestionForm, setShowQuestionForm] = useState(false)
   const [selectedQuestion, setSelectedQuestion] = useState<
      ICampaignQuestions | undefined
   >(undefined)
   const [detailsCharCount, setDetailsCharCount] = useState(0)
   const [form] = Form.useForm()
   const [sellerQuestions, setSellerQuestions] = useState<ICampaignQuestions[]>(
      []
   )
   const [buyerQuestions, setBuyerQuestions] = useState<ICampaignQuestions[]>(
      []
   )
   const { mutate: mutateActiveLeadSource } = useGetActiveLeadSource(
      title || ''
   )

   useEffect(() => {
      if (!isLoading && CampaignDetails?.data?.qualifyingQuestions) {
         const sellerQs = CampaignDetails.data?.qualifyingQuestions
            .filter((q) => q.type === 'seller')
            .sort((a, b) => a.order - b.order)
         const buyerQs = CampaignDetails.data?.qualifyingQuestions
            .filter((q) => q.type === 'buyer')
            .sort((a, b) => a.order - b.order)
         setSellerQuestions(sellerQs)
         setBuyerQuestions(buyerQs)
      }
   }, [CampaignDetails, isLoading])

   useEffect(() => {
      if (selectedQuestion) {
         form.setFieldsValue(selectedQuestion)
         setDetailsCharCount(selectedQuestion.question.length)
      }
   }, [selectedQuestion, form])

   const moveQuestion = useCallback(
      async (result: any, isSeller: boolean) => {
         if (!result.destination) return

         const questions = isSeller ? sellerQuestions : buyerQuestions
         const reorderedQuestions = Array.from(questions)
         const [movedItem] = reorderedQuestions.splice(result.source.index, 1)
         reorderedQuestions.splice(result.destination.index, 0, movedItem)

         const updatedQuestions = reorderedQuestions.map((item, index) => ({
            ...item,
            order: index + 1,
         }))

         try {
            const updatedFromServer =
               await CampaignService.updateCampaignQualifyingQuestions(
                  title as string,
                  isSeller
                     ? [...updatedQuestions, ...buyerQuestions]
                     : [...sellerQuestions, ...updatedQuestions]
               )
            if (updatedFromServer?.qualifyingQuestions) {
               if (isSeller) {
                  setSellerQuestions(
                     updatedFromServer.qualifyingQuestions.filter(
                        (item) => item.type === 'seller'
                     )
                  )
               } else {
                  setBuyerQuestions(
                     updatedFromServer.qualifyingQuestions.filter(
                        (item) => item.type === 'buyer'
                     )
                  )
               }
            }
            mutate()
         } catch (error) {
            console.error('Failed to update question order:', error)
            message.error('Failed to update question order')
         }
      },
      [buyerQuestions, sellerQuestions, title, mutate]
   )

   const handleDeleteQuestion = useCallback(
      async (question: ICampaignQuestions) => {
         try {
            const questions =
               option === 'seller' ? sellerQuestions : buyerQuestions

            // Check if this is the last question
            if (questions.length <= 1) {
               message.warning(
                  'At least one qualifying question is required to screen leads.'
               )
               return
            }

            const updatedQuestions = questions.filter(
               (item) => item !== question
            )

            const updatedFromServer =
               await CampaignService.updateCampaignQualifyingQuestions(
                  title as string,
                  option === 'seller'
                     ? [...updatedQuestions, ...buyerQuestions]
                     : [...sellerQuestions, ...updatedQuestions]
               )

            if (updatedFromServer?.qualifyingQuestions) {
               if (option === 'seller') {
                  setSellerQuestions(
                     updatedFromServer.qualifyingQuestions.filter(
                        (item) => item.type === 'seller'
                     )
                  )
               } else {
                  setBuyerQuestions(
                     updatedFromServer.qualifyingQuestions.filter(
                        (item) => item.type === 'buyer'
                     )
                  )
               }
            }
         } catch (error) {
            console.error('Failed to delete question:', error)
            message.error('Failed to delete question')
         } finally {
            setSelectedQuestion(undefined)
            mutateActiveLeadSource()
         }
      },
      [buyerQuestions, sellerQuestions, option, title]
   )

   const handleSubmit = useCallback(async () => {
      const values = await form.validateFields()
      if (values) {
         setButtonLoading(true)
         const questionType = selectedQuestion ? selectedQuestion.type : option
         const order = selectedQuestion
            ? selectedQuestion.order
            : questionType === 'buyer'
            ? buyerQuestions.length + 1
            : sellerQuestions.length + 1

         const updatedQuestion = {
            type: questionType,
            order: order,
            question: values.question,
         }

         let updatedBuyerQuestions = [...buyerQuestions]
         let updatedSellerQuestions = [...sellerQuestions]

         if (selectedQuestion) {
            if (questionType === 'buyer') {
               updatedBuyerQuestions = buyerQuestions.map((q) =>
                  q === selectedQuestion ? updatedQuestion : q
               )
            } else {
               updatedSellerQuestions = sellerQuestions.map((q) =>
                  q === selectedQuestion ? updatedQuestion : q
               )
            }
         } else {
            if (questionType === 'buyer') {
               updatedBuyerQuestions = [...buyerQuestions, updatedQuestion]
            } else {
               updatedSellerQuestions = [...sellerQuestions, updatedQuestion]
            }
         }

         try {
            const response =
               await CampaignService.updateCampaignQualifyingQuestions(
                  title as string,
                  questionType === 'buyer'
                     ? [...updatedBuyerQuestions, ...sellerQuestions]
                     : [...buyerQuestions, ...updatedSellerQuestions]
               )

            if (response && response.qualifyingQuestions) {
               setBuyerQuestions(
                  response.qualifyingQuestions.filter(
                     (item) => item.type === 'buyer'
                  )
               )
               setSellerQuestions(
                  response.qualifyingQuestions.filter(
                     (item) => item.type === 'seller'
                  )
               )
               setSelectedQuestion(undefined)
               form.resetFields()
               setDetailsCharCount(0)
               mutate()
               setButtonLoading(false)
            }
         } catch (error) {
            console.error('Failed to update questions:', error)
            message.error('Failed to update questions')
         } finally {
            setButtonLoading(false)
         }
      }
   }, [
      buyerQuestions,
      sellerQuestions,
      selectedQuestion,
      option,
      title,
      form,
      mutate,
   ])

   return (
      <div className="flex flex-col flex-1">
         <div className="px-10 py-5 font-medium">Qualifying questions</div>
         <div className="bg-primary/5">
            <div className="flex items-center px-10 py-5 text-[12px] text-white/70 gap-3 bg-primary/10 font-medium">
               View or edit qualifying questions for Buyers/Sellers
            </div>
         </div>
         <div className="flex items-center justify-center px-10 py-5 text-[12px] bg-primary/10 font-medium border-t border-b border-black/20">
            <button
               onClick={() => setOption('buyer')}
               className={`px-8 py-5 cursor-pointer ${
                  option === 'buyer'
                     ? 'bg-primary'
                     : 'text-white/50 bg-primary/10'
               }`}
            >
               Buyer Qualifying
            </button>
            <button
               onClick={() => setOption('seller')}
               className={`px-8 py-5 cursor-pointer ${
                  option === 'seller'
                     ? 'bg-primary'
                     : 'text-white/50 bg-primary/10'
               }`}
            >
               Seller Qualifying
            </button>
         </div>
         <div
            onClick={() => setShowQuestionForm(!showQuestionForm)}
            className="px-10 py-5 mb-[2px] group font-medium border-t border-b cursor-pointer flexc bg-primary/20 border-black/20"
         >
            <div className="transition-all opacity-50 cursor-pointer flexc group-hover:opacity-100">
               <Icon name="Plus" className="mr-2 scale-75" />
               <span>Add Question</span>
            </div>
         </div>

         <DndProvider backend={HTML5Backend}>
            <div className="flex flex-col flex-1 gap-1">
               {(option === 'buyer' ? buyerQuestions : sellerQuestions).map(
                  (item, index) => (
                     <DraggableQuestion
                        key={item.order}
                        item={item}
                        index={index}
                        onDelete={() => handleDeleteQuestion(item)}
                        onClick={() => {
                           setShowQuestionForm(true)
                           setSelectedQuestion(item)
                        }}
                        moveQuestion={(fromIndex, toIndex) =>
                           moveQuestion(
                              {
                                 source: {
                                    droppableId: option,
                                    index: fromIndex,
                                 },
                                 destination: {
                                    droppableId: option,
                                    index: toIndex,
                                 },
                                 reason: 'DROP',
                                 combine: null,
                                 mode: 'FLUID',
                                 draggableId: index.toString(),
                                 type: 'DEFAULT',
                              },
                              option === 'seller'
                           )
                        }
                     />
                  )
               )}
            </div>
         </DndProvider>

         {showQuestionForm && (
            <QuestionSubmitForm
               selectedQuestion={selectedQuestion}
               isLoading={isButtonLoading}
               detailsCharCount={detailsCharCount}
               form={form}
               handleSubmit={handleSubmit}
               setDetailsCharCount={setDetailsCharCount}
            />
         )}
      </div>
   )
}
export default QualifyingQuestions
